import React, { useEffect, useState } from 'react';

import { Text } from '@mantine/core';
import Table from 'components/Table';
import { useLocalization } from 'context/localization-context';
import usePayrollReport from 'hooks/reports/payroll/usePayrollReport';
import { CellProps } from 'react-table';
import { PayrollReport } from 'types';

const PayrollReportTable = ({
  payrollPeriod,
  handleOpenDetail,
  setOrderingValue,
}: {
  payrollPeriod: string | null;
  setOrderingValue: React.Dispatch<React.SetStateAction<string>>;
  handleOpenDetail: (staff: PayrollReport) => void;
}): JSX.Element => {
  const { formatCurrency } = useLocalization();

  const [tableData, setTableData] = useState<PayrollReport[]>([]);
  const [ordering, setOrdering] = useState('staffName');
  const [ascending, setAscending] = useState(true);

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    usePayrollReport({
      payrollPeriod,
      ordering: ascending ? ordering : `-${ordering}`,
    });

  const handleHeaderClick = (header: string): void => {
    setOrdering(header);
    setAscending(a => {
      setOrderingValue(!a ? header : `-${header}`);
      return !a;
    });
  };

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  const columns = [
    {
      Header: 'Staff Name',
      accessor: 'staffName' as const,
      Cell: ({ row }: CellProps<PayrollReport>) => (
        <Text
          weight='bold'
          underline
          sx={{ cursor: 'pointer', width: 'fit-content' }}
          onClick={() => handleOpenDetail(row.original)}
          data-cy='staffPayrollReportRow'
        >
          {row.original.staffName}
        </Text>
      ),
    },
    {
      Header: 'Total Payout',
      accessor: 'payoutAmount' as const,
      Cell: ({ value }: CellProps<PayrollReport>) => formatCurrency(value / 100),
    },
  ];

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={['staffName', 'payoutAmount']}
        handleHeaderClick={handleHeaderClick}
        ordering={ordering}
        ascending={ascending}
      />
    </>
  );
};

export default PayrollReportTable;
